import { useEffect } from 'react';
import { Signal, useComputed, useSignal } from '@preact/signals-react';
import {
  AiComponentHtmlAttribute,
  AiComponentJsVariable,
  AiComponentVisualManifest,
} from '@/pkg/ai';
import { useAiComponentContext } from '@/features/editor/widgets/shared/ai-editing-layout/context';

export function useManifestSearch(
  manifest: Signal<AiComponentVisualManifest>,
  logicOnly: boolean,
) {
  const searchQuery = useSignal(``);
  const debouncedSearchQuery = useSignal(``);

  useEffect(() => {
    const timeout = setTimeout(() => {
      debouncedSearchQuery.value = searchQuery.value;
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery.value]);

  const getAttribute = useAiComponentContext((ctx) => ctx.getAttribute);
  const getCssRule = useAiComponentContext((ctx) => ctx.getCssRule);
  const getJsVariables = useAiComponentContext(
    (ctx) => ctx.getManifestVariables,
  );
  const getTextContent = useAiComponentContext((ctx) => ctx.getTextContent);
  const getVideoAttributes = useAiComponentContext(
    (ctx) => ctx.getVideoAttributes,
  );

  const filteredComponents = useComputed(() => {
    if (!manifest.value || !manifest.value.components) return [];

    let { components } = manifest.value;

    if (logicOnly) {
      components = components.filter((comp) => {
        if (comp.type === `js-variable`) return true;
        if (comp.type === `html-video`) return true;
        return (
          comp.type === `html-attr` &&
          (comp as AiComponentHtmlAttribute).attr === `href`
        );
      });
    } else {
      components = components.filter((comp) => comp.type !== `js-variable`);
    }

    if (debouncedSearchQuery.value.trim()) {
      const query = debouncedSearchQuery.value.toLowerCase().trim();
      components = components.filter((comp) => {
        const basicMatch =
          comp.name.toLowerCase().includes(query) ||
          comp.group?.toLowerCase().includes(query) ||
          comp.description?.toLowerCase().includes(query) ||
          comp.selector.toLowerCase().includes(query);

        if (basicMatch) return true;

        try {
          if (comp.type === `html-content`) {
            const text = getTextContent(comp.selector);
            if (text?.toLowerCase().includes(query)) return true;
          }
          if (comp.type === `html-attr`) {
            const val = getAttribute(
              comp.selector,
              comp as AiComponentHtmlAttribute,
            );
            if (val?.toLowerCase().includes(query)) return true;
          }
          if (comp.type === `js-variable`) {
            const jsVar = comp as AiComponentJsVariable;
            const vars = getJsVariables();
            const v = vars.find((x) => x.variable === jsVar.variable);
            if (
              String(v?.value ?? jsVar.value)
                .toLowerCase()
                .includes(query)
            ) {
              return true;
            }
          }
          if (comp.type === `css-rule`) {
            const rules = getCssRule(comp.selector);
            if (
              rules.declarations.some(
                (decl) =>
                  decl.name.toLowerCase().includes(query) ||
                  decl.value.toLowerCase().includes(query),
              )
            ) {
              return true;
            }
          }
          if (comp.type === `html-video`) {
            const video = getVideoAttributes(comp.selector);
            if (video?.source?.toLowerCase().includes(query)) return true;
          }
        } catch (e) {
          // ignore error
        }
        return false;
      });
    }

    return components;
  });

  return {
    searchQuery,
    filteredComponents,
  };
}
